import React from "react";
import { useSelector } from "react-redux";
import Modal from "../../components/Modal";
import LandlordRepRequirementForm from "../../LandlordRep/LandlordRepRequirementForm";
import { useEffect } from "react";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaUserTie,
  FaUsers,
  FaIndustry,
  FaCalendarAlt,
  FaChartLine,
  FaPlus,
  FaSearch,
  FaThList,
  FaTh,
  FaInfoCircle,
  FaFilter,
} from "react-icons/fa";
import { BiSearchAlt } from "react-icons/bi";

import LandlordRepREqCard from "../../LandlordRep/LandlordRepREqCard";
import { getAllDocs, getAllDocsSnap } from "../../Firebase/Firebase.utils";
import { useNavigate } from "react-router-dom";
import UserAvatar from "../../components/UserAvatar";

// Card Component
function LandlordCard({ requirement, onClick }) {
  // Calculate days elapsed since creation
  const daysElapsed = requirement.createdAt
    ? Math.floor(
        (new Date() - new Date(requirement.createdAt)) / (1000 * 60 * 60 * 24)
      )
    : 0;

  // Determine status style
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "on hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer h-full flex flex-col"
      onClick={onClick}
    >
      {/* Card Header */}
      <div className="bg-gradient-to-r from-[#002368] to-[#002368]/90 text-white p-4 relative">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1">
              {requirement.tenantName}
            </h3>
            <div className="flex items-center text-xs text-white/80">
              <FaBuilding className="mr-1" />{" "}
              {requirement.buildingName || "Building not specified"}
            </div>
          </div>

          {/* Status Badge */}
          <div
            className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusStyle(
              requirement.status || "active"
            )}`}
          >
            {requirement.status || "Active"}
          </div>
        </div>

        {/* Pipeline Status if available */}
        {requirement.pipeline_status && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span>Pipeline Progress</span>
              <span>{requirement.pipeline_status}</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div
                className="bg-[#ff5003] h-1.5 rounded-full"
                style={{ width: requirement.pipeline_status || "10%" }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 flex-1">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-start">
            <FaBuilding className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Size</div>
              <div className="font-medium">
                {requirement.requirementSize || "Not specified"}
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FaMapMarkerAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Location</div>
              <div className="font-medium">
                {requirement.location || "Not specified"}
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FaUserTie className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Contact</div>
              <div className="font-medium">
                {requirement.contactPerson || "Not specified"}
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FaCalendarAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Created</div>
              <div className="font-medium">
                {requirement.createdAt
                  ? new Date(requirement.createdAt).toLocaleDateString()
                  : "Not specified"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="border-t border-gray-100 p-3 bg-gray-50 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {daysElapsed === 0 ? "Added today" : `${daysElapsed} days ago`}
        </div>

        <div className="flex -space-x-2">
          {requirement.team_members?.map(
            (member, index) =>
              index < 3 && (
                <div key={index} className="flex items-center">
                  <UserAvatar email={member} size="sm" />
                </div>
              )
          )}
          {!requirement.team_members && requirement.createdBy && (
            <UserAvatar email={requirement.createdBy} size="sm" />
          )}
          {requirement.team_members?.length > 3 && (
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-700 border border-white">
              +{requirement.team_members.length - 3}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// List Item Component
function LandlordListItem({ requirement, onClick }) {
  // Calculate days elapsed since creation
  const daysElapsed = requirement.createdAt
    ? Math.floor(
        (new Date() - new Date(requirement.createdAt)) / (1000 * 60 * 60 * 24)
      )
    : 0;

  // Determine status style
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "on hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div
      className="bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={onClick}
    >
      <div className="flex flex-col md:flex-row">
        {/* Left Section with Key Info */}
        <div className="md:w-64 bg-gradient-to-r from-[#002368] to-[#002368]/90 text-white p-4 flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1 line-clamp-2">
              {requirement.tenantName}
            </h3>
            <div className="flex items-center text-xs text-white/80 mb-2">
              <FaBuilding className="mr-1" />{" "}
              {requirement.buildingName || "Building not specified"}
            </div>

            {/* Status Badge */}
            <div
              className={`inline-block text-xs px-2 py-1 rounded-full font-medium mt-1 ${getStatusStyle(
                requirement.status || "active"
              )}`}
            >
              {requirement.status || "Active"}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4">
          {/* Middle Row - Requirement Details */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div className="flex items-start">
              <FaBuilding className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Size</div>
                <div className="font-medium">
                  {requirement.requirementSize || "Not specified"}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <FaMapMarkerAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Location</div>
                <div className="font-medium">
                  {requirement.location || "Not specified"}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <FaCalendarAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Created</div>
                <div className="font-medium">
                  {requirement.createdAt
                    ? new Date(requirement.createdAt).toLocaleDateString()
                    : "Not specified"}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row - Team & Actions */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-500 mr-2">Team:</div>
              <div className="flex items-center flex-wrap">
                {requirement.team_members?.map(
                  (member, index) =>
                    index < 4 && (
                      <div key={index} className="inline-block ml-1 first:ml-0">
                        <UserAvatar email={member} size="sm" />
                      </div>
                    )
                )}
                {!requirement.team_members && requirement.createdBy && (
                  <UserAvatar email={requirement.createdBy} size="sm" />
                )}
                {requirement.team_members?.length > 4 && (
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-700 border border-white ml-1">
                    +{requirement.team_members.length - 4}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {daysElapsed === 0 ? "Added today" : `${daysElapsed} days ago`}
              </div>
              <button className="flex items-center gap-1 bg-white border border-[#002368] text-[#002368] px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-[#002368] hover:text-white transition-colors duration-200">
                <FaInfoCircle size={14} /> Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandlordRep() {
  const currentUser = useSelector((state) => state.userObject);
  const [addMode, setAddMode] = React.useState(false);
  const [requirements, setRequirements] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [viewMode, setViewMode] = React.useState("cards"); // 'cards' or 'list'
  const [searchTerm, setSearchTerm] = React.useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRequirements = async () => {
      await getAllDocsSnap("landlordRepRequirements", (res) => {
        setRequirements(res);
        setIsLoading(false);
      });
    };
    fetchRequirements();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#002368]"></div>
      </div>
    );
  }

  // Sort requirements by createdAt date
  const sortedRequirements = [...requirements].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  // Filter requirements based on search term
  const filteredRequirements = sortedRequirements.filter((req) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (req.tenantName && req.tenantName.toLowerCase().includes(searchLower)) ||
      (req.buildingName &&
        req.buildingName.toLowerCase().includes(searchLower)) ||
      (req.requirementSize &&
        req.requirementSize.toLowerCase().includes(searchLower)) ||
      (req.location && req.location.toLowerCase().includes(searchLower))
    );
  });

  const handleAddMode = () => {
    setAddMode(!addMode);
  };

  const handleNavigate = (id) => {
    navigate(`/leasing/landlordrepresentation/${id}?link=Requirement+Details`);
  };

  // Stats summary
  const stats = [
    {
      name: "All Accounts",
      count: requirements.length,
      icon: <FaBuilding />,
    },
    {
      name: "Active Accounts",
      count: requirements.filter((r) => r.status !== "completed").length,
      icon: <FaUserTie />,
    },
    {
      name: "Completed Accounts",
      count: requirements.filter((r) => r.status === "completed").length,
      icon: <FaUsers />,
    },
  ];

  return (
    <div className="flex flex-col">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8 mt-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-4 rounded-lg cursor-pointer border transition-all duration-300 bg-white border-gray-100 hover:border-gray-300"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#002368]/10 text-[#002368]">
                {stat.icon}
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600">
                  {stat.name}
                </h3>
                <p className="text-2xl font-bold text-[#002368]">
                  {stat.count}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search and Actions Header */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="relative w-full md:w-2/3">
          <input
            type="search"
            placeholder="Search accounts by name, building, size, or location..."
            className="border border-gray-200 p-3 pl-12 rounded-lg w-full focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("cards")}
              className={`flex items-center gap-1 py-2.5 px-3 ${
                viewMode === "cards"
                  ? "bg-[#002368] text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaTh size={14} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1 py-2.5 px-3 ${
                viewMode === "list"
                  ? "bg-[#002368] text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaThList size={14} />
            </button>
          </div>

          <button
            className="bg-[#002368] text-white rounded-md px-4 py-2.5 flex items-center gap-2 hover:bg-[#001a4d] transition-colors"
            onClick={handleAddMode}
          >
            <FaPlus size={14} />
            <span>Add an Account</span>
          </button>
        </div>
      </div>

      {/* Accounts List Section */}
      <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-800">
            Landlord Accounts
          </h2>
          <div className="text-sm text-gray-500">
            Showing {filteredRequirements.length} of {requirements.length}{" "}
            accounts
          </div>
        </div>

        {/* Empty state */}
        {filteredRequirements.length === 0 && (
          <div className="text-center py-12">
            <FaBuilding className="text-gray-200 text-5xl mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">
              No accounts found
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm
                ? "Try adjusting your search or filters"
                : "Create your first landlord account to get started"}
            </p>
            {!searchTerm && (
              <button
                className="bg-[#002368] text-white rounded-md px-4 py-2.5 flex items-center gap-2 hover:bg-[#001a4d] transition-colors mx-auto"
                onClick={handleAddMode}
              >
                <FaPlus size={14} />
                <span>Add an Account</span>
              </button>
            )}
          </div>
        )}

        {/* Grid View */}
        {filteredRequirements.length > 0 && viewMode === "cards" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRequirements.map((requirement) => (
              <LandlordCard
                key={requirement.id}
                requirement={requirement}
                onClick={() => handleNavigate(requirement.id)}
              />
            ))}
          </div>
        )}

        {/* List View */}
        {filteredRequirements.length > 0 && viewMode === "list" && (
          <div className="flex flex-col gap-3">
            {filteredRequirements.map((requirement) => (
              <LandlordListItem
                key={requirement.id}
                requirement={requirement}
                onClick={() => handleNavigate(requirement.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        {addMode && (
          <Modal>
            <LandlordRepRequirementForm handleAddMode={handleAddMode} />
          </Modal>
        )}
      </div>
    </div>
  );
}
export default LandlordRep;
